import { captureRemixErrorBoundaryError } from "@sentry/remix";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from "@remix-run/react";

import styles from "~/tailwind.css?url";
import type {
  ActionFunction,
  LinksFunction,
  LoaderFunctionArgs,
  SerializeFrom,
} from "@remix-run/cloudflare";
import { json, redirect } from "@remix-run/cloudflare";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { ShoppingCart } from "./components/ShoppingCart";
import MainMenu from "./components/MainMenu";
import { ModalProvider, useModal } from "./context/Modal";
import {
  USER_SESSION_KEY,
  commitSession,
  getSession,
  getUser,
} from "./session.server";
import {
  changeItemQuantity,
  getCartOrUseInMemoryCart,
  removeFromCart,
  updateCartPrices,
} from "./cart.server";
import type { CartItem } from "../../types/CartItem";
import { INTENTS } from "./utils/intents";
import { getAllCategoriesWithSubCategories } from "./category.server";
import { getBrands } from "./routes/shop/queries";
import Search from "./components/Search";
import { cookieConsent, cookieIsClosed } from "./cookies.server";
import { CookieConsent } from "./components/CookieConsent";
import animations from "~/animations.css?url";
import linkAnimations from "~/linkAnimations.css?url";
import {
  ExternalScripts,
  ExternalScriptsHandle,
} from "remix-utils/external-scripts";
import { useGA } from "./hooks/useGA";
import { Inventory } from "../../types/Inventory";
import { NOLA_CLUB_ID, NOLA_HOLIDAY_CALENDAR_ID, NOLA_SINGLES_DAY_ID } from "./utils/constants";
import { addProfileToList, createProfile, findProfile } from "./klaviyo.server";
import { NolaIsClosed } from "./NolaIsClosed";

type LoaderData = SerializeFrom<typeof loader>;

export let handle: ExternalScriptsHandle<LoaderData> = {
  scripts(args) {
    const gaTrackingId = args.data.gaTrackingId;
    if (args.location.pathname === "/") {
      return [
        {
          type: "text/javascript",
          src: `https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`,
          async: false,
          crossOrigin: "anonymous",
        },
      ];
    }
    return [
      {
        type: "text/javascript",
        src: `https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`,
        async: false,
        crossOrigin: "anonymous",
      },
    ];
  },
};

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: styles },
  { rel: "stylesheet", href: animations },
  { rel: "stylesheet", href: linkAnimations },
];

export async function loader({ request, context }: LoaderFunctionArgs) {
  const cookieHeader = request.headers.get("Cookie");
  const consetCookie = await cookieConsent.parse(cookieHeader);
  const isClosedCookie = await cookieIsClosed.parse(cookieHeader);
  const user = await getUser(request);
  let cart = await getCartOrUseInMemoryCart(
    user ? user.id : "",
    context.cloudflare.env.DB,
  );
  //if cart is dirty, we need to update the prices
  if (cart.isDirty) {
    cart = await updateCartPrices(cart, context);
  }

  const session = await getSession(request);
  session.set(USER_SESSION_KEY, user);
  const categories = await getAllCategoriesWithSubCategories(
    context.cloudflare.env.DB,
  );
  const brands = await getBrands(context.cloudflare.env.DB);
  return json(
    {
      categories,
      user,
      cart,
      brands,
      gaTrackingId: context.cloudflare.env.GA_TRACKING_ID,
      showCookieConsent: consetCookie?.cookiesAccepted === undefined,
      isClosed: false,
    },
    { headers: { "set-cookie": await commitSession(session) } },
  );
}

//TODO: for now this implementation is here, but once I move the cart to a nested route,
// this should be moved to the cart route
//This is also handling the newsletter signup
export const action: ActionFunction = async ({ request, context }) => {
  const formData = await request.formData();
  const user = await getUser(request);
  const itemStr = String(formData.get("item"));
  const intent = String(formData.get("intent"));
  if (intent === "openNola") {
    const password = String(formData.get("password") ?? '/');
    const redirectUrl = String(formData.get("redirectTo"));
    if (password.toLocaleLowerCase() === "nobullshit") {
      throw redirect(redirectUrl, {
        headers: {
          "set-cookie": await cookieIsClosed.serialize({
            isClosed: false,
          }),
        },
      });
    }
    return json({ success: false, msg: "Rangt lykilorð" });
  }
  if (
    intent === INTENTS.EMAIL_SIGNUP ||
    intent === INTENTS.EMAIL_SIGNUP_FOR_SALE
  ) {
    const email = String(formData.get("email"));
    const name = String(formData.get("name"));
    const session = await getSession(request);
    const listId =
      intent === INTENTS.EMAIL_SIGNUP ? NOLA_CLUB_ID : NOLA_SINGLES_DAY_ID;
    session.set(USER_SESSION_KEY, {
      ...user,
      email,
      name,
    });
    let profileId = await findProfile(context, email);
    if (!profileId) {
      profileId = await createProfile(context, email, name);
    }
    if (profileId) {
      await addProfileToList(context, profileId, listId);
    }

    return json(
      { success: true },
      {
        headers: { "set-cookie": await commitSession(session) },
      },
    );
  }

  if (itemStr) {
    const item: CartItem = JSON.parse(itemStr);
    if (intent === INTENTS.REMOVE_FROM_SHOPPING_CART) {
      await removeFromCart(
        user.id,
        item,
        context.cloudflare.env.DB as D1Database,
      );
      return {
        success: true,
      };
    } else if (intent === INTENTS.INCREASE_QUANTITY) {
      const inventory = await context.cloudflare.env.INVENTORY.get<Inventory>(
        item.product.id,
        "json",
      );

      if (inventory) {
        const isProductSoldOut = inventory.quantity - item.quantity <= 0;
        if (isProductSoldOut) {
          return {
            success: false,
            message:
              "Því miður getum við ekki bætt við fleiri eintökum af þessari vöru í körfuna",
          };
        }
      }

      await changeItemQuantity(
        user.id,
        item,
        1,
        context.cloudflare.env.DB as D1Database,
      );
      return {
        success: true,
      };
    } else if (intent === INTENTS.DECREASE_QUANTITY) {
      await changeItemQuantity(
        user.id,
        item,
        -1,
        context.cloudflare.env.DB as D1Database,
      );
      return {
        success: true,
      };
    }
  } else {
    throw new Response("Bad Request", { status: 400 });
  }

  return {
    success: false,
  };
};

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return (
    <html lang="is">
      <head >
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta name="description" content="Nola.is - Iceland’s go-to online store for cruelty-free cosmetics and skincare. Discover premium beauty brands like Allies of Skin, ILIA, Herbivore Botanicals, and more. Shop natural, vegan, and eco-friendly beauty products with fast Icelandic shipping!" />
        <Meta />
        <Links />
      </head>
      <body>
        <div className="flex h-screen w-screen flex-col items-center justify-center">
          <div>Æææ, eitthvað fór úrskeiðis😢</div>
          <a href="https://nola.is">Ýttu hérna til að halda áfram að versla</a>
        </div>
        <Scripts />
      </body>
    </html>
  );
};


export default function App() {
  const { isClosed } = useLoaderData<{ isClosed: boolean }>();
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta name="description" content="Nola.is - Iceland’s go-to online store for cruelty-free cosmetics and skincare. Discover premium beauty brands like Allies of Skin, ILIA, Herbivore Botanicals, and more. Shop natural, vegan, and eco-friendly beauty products with fast Icelandic shipping!" />
        <Meta />
        <Links />
      </head>
      <ModalProvider>
        {isClosed ? (
          <>
            <NolaIsClosed />
            <Scripts />
          </>
        ) : (
          <InnerApp />
        )}
      </ModalProvider>
    </html>
  );
}

const InnerApp = () => {
  const { gaTrackingId } = useLoaderData<typeof loader>();
  useGA(gaTrackingId);
  const { showCookieConsent } = useLoaderData<{ showCookieConsent: boolean }>();
  const { shoppingCartVisible, mainMenuVisible, filterVisible, searchVisible } =
    useModal();

  return (
    <body
      className={
        shoppingCartVisible !== "hidden" ||
          mainMenuVisible ||
          filterVisible ||
          searchVisible
          ? "overflow-hidden"
          : ""
      }
    >
      <Header />
      <Search />
      <ShoppingCart />
      <MainMenu />
      <Outlet />
      <Footer />
      {showCookieConsent && <CookieConsent />}
      <ScrollRestoration />
      <ExternalScripts />
      <script
        async
        id="gtag-init"
        dangerouslySetInnerHTML={{
          __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                ${showCookieConsent ? "gtag('consent', 'default', {'ad_storage': 'denied','ad_user_data': 'denied','ad_personalization': 'denied','analytics_storage': 'denied'});" : ""}

                gtag('config', '${gaTrackingId}', {
                  page_path: window.location.pathname,
                });
              `,
        }}
      />

      <Scripts />
    </body>
  );
};
